import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiBaseUrl, apiFetch, downloadFile } from '@/api/client';
import { isDesktopApp } from '@/lib/desktop';
import { detectPlatform, formatBytes, type Platform } from '@/lib/platform';
import { Card, EmptyState, ErrorNote, PageHeader, Spinner } from '@/components/ui';
import { formatDate } from '@/lib/format';

interface DesktopBuild {
  id: string;
  filename: string;
  platform: Platform;
  arch: 'arm64' | 'x64' | 'universal';
  label: string;
  kind: 'installer' | 'archive';
  version: string | null;
  sizeBytes: number;
  modifiedAt: string;
  sha256: string;
  url: string;
}

interface DownloadsResponse {
  apiBaseUrl: string;
  builds: DesktopBuild[];
}

const PLATFORM_ICON: Record<Platform, string> = { mac: '🍎', windows: '⊞', linux: '🐧' };
const PLATFORM_NAME: Record<Platform, string> = { mac: 'macOS', windows: 'Windows', linux: 'Linux' };

export function DownloadPage() {
  const detected = useMemo(() => detectPlatform(), []);
  const { data, isLoading, error } = useQuery({
    queryKey: ['downloads'],
    queryFn: () => apiFetch<DownloadsResponse>('/api/downloads'),
    // Links are signed for an hour; refetch well before they lapse.
    staleTime: 30 * 60_000,
  });

  const [tab, setTab] = useState<Platform>(detected.platform ?? 'mac');

  const recommended = useMemo(() => {
    if (!data || !detected.platform) return null;
    const forPlatform = data.builds.filter((b) => b.platform === detected.platform);
    return forPlatform.find((b) => b.arch === detected.arch) ?? forPlatform[0] ?? null;
  }, [data, detected]);

  const platforms = useMemo(() => [...new Set((data?.builds ?? []).map((b) => b.platform))], [data]);
  const server = data?.apiBaseUrl ?? apiBaseUrl();
  const isLocalServer = /^https?:\/\/(localhost|127\.0\.0\.1)/.test(server);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Get the desktop app"
        description="Track time from your menu bar or system tray, with automatic screenshot capture while the timer runs."
      />

      {isDesktopApp() && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900">
          You are already using the desktop app. This page is here so you can share it with colleagues.
        </div>
      )}

      <ErrorNote error={error} />

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : (data?.builds.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            title="No desktop builds published yet"
            description="Once someone runs the packaging command, the installers appear here automatically."
          />
          <div className="border-t border-slate-100 px-5 py-4">
            <p className="text-xs text-slate-500">Build them with:</p>
            <pre className="mt-1.5 overflow-x-auto rounded-lg bg-slate-900 px-3 py-2 text-xs text-slate-100">
{`npm run package:all -w @chronos/desktop`}
            </pre>
            <p className="mt-2 text-xs text-slate-500">
              The API serves whatever it finds in <code className="rounded bg-slate-100 px-1">DESKTOP_BUILDS_DIR</code>.
            </p>
          </div>
        </Card>
      ) : (
        <>
          {recommended && (
            <Card className="mb-4 overflow-hidden">
              <div className="flex flex-wrap items-center gap-4 bg-gradient-to-br from-brand-600 to-brand-700 px-6 py-5 text-white">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-white/15 text-2xl" aria-hidden="true">
                  {PLATFORM_ICON[recommended.platform]}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-white/70">Recommended for {detected.label}</p>
                  <p className="text-lg font-semibold">TickTock {recommended.version ?? ''} · {recommended.label}</p>
                  <p className="text-xs text-white/70">
                    {recommended.filename} · {formatBytes(recommended.sizeBytes)}
                  </p>
                </div>
                <a href={recommended.url} download className="btn shrink-0 bg-white px-5 py-2.5 font-semibold text-brand-700 hover:bg-brand-50">
                  Download
                </a>
              </div>
            </Card>
          )}

          <Card className="mb-4">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
              <h2 className="text-sm font-semibold text-slate-800">All builds</h2>
              <div className="flex rounded-lg border border-slate-300 p-0.5">
                {platforms.map((p) => (
                  <button
                    key={p}
                    onClick={() => setTab(p)}
                    className={`rounded-md px-3 py-1 text-xs transition ${tab === p ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    {PLATFORM_NAME[p]}
                  </button>
                ))}
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {data?.builds.filter((b) => b.platform === tab).map((b) => (
                <li key={b.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800">{b.label}</p>
                    <p className="truncate font-mono text-xs text-slate-500">{b.filename}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {formatBytes(b.sizeBytes)} · built {formatDate(b.modifiedAt)} ·{' '}
                      <span className="font-mono" title={`SHA-256: ${b.sha256}`}>sha256 {b.sha256.slice(0, 12)}…</span>
                    </p>
                  </div>
                  <a href={b.url} download className="btn-secondary shrink-0 py-1.5 text-xs">Download</a>
                </li>
              ))}
            </ul>
          </Card>

          <ServerConfigCard server={server} isLocalServer={isLocalServer} />
          <SetupInstructions platform={tab} />
        </>
      )}
    </div>
  );
}

function ServerConfigCard({ server, isLocalServer }: { server: string; isLocalServer: boolean }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const snippet = JSON.stringify({ apiBaseUrl: server }, null, 2);

  return (
    <Card className="mb-4 p-5">
      <h2 className="text-sm font-semibold text-slate-800">Server</h2>
      <p className="mt-1 text-sm text-slate-600">
        Installers already point at <span className="font-mono text-slate-800">{server}</span>. Download, install, sign in — there
        is nothing to configure.
      </p>

      {isLocalServer && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          This server is <span className="font-mono">{server}</span>, which only resolves on this machine. Colleagues on other
          computers need TickTock deployed at a reachable address, and <code>API_BASE_URL</code> set to it, before these installs
          will connect.
        </p>
      )}

      <details className="mt-3 text-sm">
        <summary className="cursor-pointer text-xs font-medium text-slate-600 hover:text-slate-900">
          Pointing an install at a different server
        </summary>
        <p className="mt-2 text-xs text-slate-600">
          Only needed to aim a copy at another deployment — a staging server, say. Save this as{' '}
          <code className="rounded bg-slate-100 px-1 font-mono">ticktock.config.json</code> beside the installed app, or set{' '}
          <code className="rounded bg-slate-100 px-1 font-mono">CHRONOS_API_URL</code>. Either overrides the built-in address.
        </p>

      <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-900 px-3 py-2.5 text-xs text-slate-100">{snippet}</pre>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          className="btn-secondary py-1.5 text-xs"
          onClick={() => {
            setDownloading(true);
            void downloadFile('/api/downloads/config', undefined, 'ticktock.config.json').finally(() => setDownloading(false));
          }}
          disabled={downloading}
        >
          {downloading && <Spinner className="h-3 w-3" />} Download ticktock.config.json
        </button>
        <button
          className="btn-ghost py-1.5 text-xs"
          onClick={() => {
            void navigator.clipboard?.writeText(snippet).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <dl className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-500">
        <div>
          <dt className="inline font-medium text-slate-600">macOS: </dt>
          <dd className="inline font-mono">/Applications/TickTock.app/Contents/MacOS/ticktock.config.json</dd>
        </div>
        <div>
          <dt className="inline font-medium text-slate-600">Windows: </dt>
          <dd className="inline font-mono">%LOCALAPPDATA%\Programs\TickTock\ticktock.config.json</dd>
        </div>
      </dl>
      </details>
    </Card>
  );
}

function SetupInstructions({ platform }: { platform: Platform }) {
  const steps: Record<Platform, { title: string; body: React.ReactNode }[]> = {
    mac: [
      { title: 'Install', body: <>Open the <span className="font-mono">.dmg</span> and drag TickTock to Applications, or unzip the archive into <span className="font-mono">/Applications</span>.</> },
      {
        title: 'First launch',
        body: (
          <>
            These builds are unsigned, so macOS quarantines them and reports{' '}
            <i>"TickTock is damaged and can't be opened"</i>. It is not damaged — clear the quarantine flag, then open it
            normally:
            <pre className="mt-1.5 overflow-x-auto rounded bg-slate-900 px-2.5 py-1.5 text-xs text-slate-100">xattr -dr com.apple.quarantine /Applications/TickTock.app</pre>
            <span className="mt-1 block text-xs text-slate-500">
              Adjust the path if you kept it elsewhere, e.g. <span className="font-mono">~/Downloads/TickTock.app</span>. Right-click
              → Open does <b>not</b> clear this particular error.
            </span>
          </>
        ),
      },
      { title: 'Allow screen recording', body: <>System Settings → Privacy &amp; Security → <b>Screen Recording</b> → enable TickTock, then restart the app. Without it the timer still works, but no screenshots are captured.</> },
      { title: 'Sign in', body: <>Use the same username and password as this web app. Accept the monitoring notice, pick a project, and start the timer.</> },
    ],
    windows: [
      { title: 'Install', body: <>Run <span className="font-mono">TickTock Setup.exe</span>. It installs per-user, so no administrator rights are needed.</> },
      { title: 'SmartScreen', body: <>These builds are unsigned, so Windows shows "Windows protected your PC". Click <b>More info</b> → <b>Run anyway</b>.</> },
      { title: 'Sign in', body: <>Use the same username and password as this web app. Accept the monitoring notice, pick a project, and start the timer.</> },
      { title: 'Screenshots', body: <>No extra permission is needed on Windows. Captures begin one interval after you clock in.</> },
    ],
    linux: [
      { title: 'Install', body: <>Make the AppImage executable with <span className="font-mono">chmod +x</span> and run it.</> },
      { title: 'Sign in', body: <>Use the same username and password as this web app.</> },
    ],
  };

  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold text-slate-800">Setting it up on {PLATFORM_NAME[platform]}</h2>
      <ol className="mt-3 space-y-3">
        {steps[platform].map((step, i) => (
          <li key={step.title} className="flex gap-3">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-800">{step.title}</p>
              <div className="mt-0.5 text-sm text-slate-600">{step.body}</div>
            </div>
          </li>
        ))}
      </ol>

      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-600">
        <p className="font-medium text-slate-700">What the desktop app does that the web app doesn't</p>
        <p className="mt-1">
          It captures a screenshot of your screens on a fixed interval while your timer runs, notifies you each time, and records how
          much keyboard and mouse activity occurred — never what you typed. Nothing is captured while you are clocked out. You can
          review everything captured about you under <b>My activity</b>.
        </p>
      </div>
    </Card>
  );
}
